// Engine per-assignment failure codes → what the TEACHER should do.
//
// GENERATED-SHAPED, HAND-MAINTAINED. The code list mirrors the engine's
// `src/platonica_alpha/domain/error_codes.py::CALLBACK_ERROR_CODES` (91
// codes). `apps/backend/core/engine_contract_lock.json` carries the same
// list as engine-owned data, and
// `apps/backend/tests/core/test_engine_error_taxonomy.py` fails when this
// file and that lock disagree — so a code added on the engine side cannot
// silently land here unclassified.
//
// WHY BUCKETS INSTEAD OF ONE MESSAGE PER CODE
//
// A teacher does not need to know WHICH internal check failed — she needs
// to know what to DO. `manifest_student_render_projection_mismatch` and
// `no_manifest_produced` are the same event from her chair: the material
// didn't come out right, generate it again.
//
// WHY "temporary" IS NO LONGER THE FALLBACK
//
// It used to be. This file knew 35 of the engine's codes and sent the other
// 56 to "temporary" — "try again" — which rendered a Retry button for
// failures that can never succeed on retry: an unsupported university
// course, a language the engine cannot generate, a missing grading bundle.
// The partition is now total and asserted, and an UNKNOWN code (one the
// engine added and nobody classified) is its own bucket: it reads as a
// generic failure and is logged, rather than promising a retry that cannot
// work. Audit 2026-09-07, F-ENG-1.

/**
 * What the teacher can do about it. The bucket picks the copy, the tone,
 * and which action button appears.
 */
export type MaterialFailureKind =
  /** Transient. The same request will probably work on a retry. */
  | "temporary"
  /** The REQUEST is wrong. Retrying it unchanged cannot succeed. */
  | "input"
  /** Generated, but didn't pass our own checks. Regenerate. */
  | "quality"
  /** Deployment or institutional setup. Retrying cannot enable it. */
  | "configuration"
  /** Not in the registry this build knows about. Never promises a retry. */
  | "unknown"

/**
 * The request itself was rejected — retrying identical inputs re-runs the
 * same rejection. These get a corrective CTA, never a retry button.
 * Includes the per-course rejections (topic / material type / item type /
 * difficulty / generation mode not offered by that university course):
 * the professor picks something else, which is a request change.
 */
const INPUT_REJECTION_CODES = [
  "course_difficulty_not_supported",
  "course_generation_mode_not_supported",
  "course_item_type_not_supported",
  "course_material_type_not_supported",
  "course_topic_not_supported",
  "education_level_invalid",
  "item_count_invalid",
  "item_count_too_large",
  "item_count_too_small",
  "item_type_invalid",
  "item_type_language_incompatible",
  "item_type_required",
  "language_invalid",
  "language_not_yet_supported",
  "language_required",
  "shared_question_fraction_invalid",
  "shared_question_fraction_requires_enabled",
  "shared_questions_enabled_invalid",
  "shared_questions_invalid",
  "shared_questions_not_supported",
  "shared_questions_subject_not_supported",
  "topic_required",
  "topic_subject_mismatch",
  "topic_subject_override_invalid",
  "type_invalid",
] as const

/**
 * The material was produced but failed a publish-time check, so we never
 * released it. Regenerating usually clears it — the content differs each
 * run. Includes the whole `manifest_*` family, the release/upload failures
 * and the lineage projections, collapsed on purpose: the distinction
 * between them is meaningless outside the engine.
 */
const QUALITY_GATE_CODES = [
  "artifact_release_path_mismatch",
  "artifact_storage_layout_unsupported",
  "artifact_upload_incomplete",
  "final_validation_not_publishable",
  // The engine generated a candidate and its own publication gate refused
  // it (route authority). Not the request, not the institution: regenerate.
  "generation_route_authority_rejected",
  "international_economics_lineage_missing",
  "international_economics_lineage_projection_failed",
  "international_economics_lineage_unexpected",
  "manifest_artifact_identity_invalid",
  "manifest_content_free_diagnostic_invalid",
  "manifest_education_level_invalid",
  "manifest_education_level_mismatch",
  "manifest_education_level_provider_mismatch",
  "manifest_identity_mismatch",
  "manifest_international_economics_contract_invalid",
  "manifest_international_economics_contract_mismatch",
  "manifest_invalid",
  "manifest_learning_evidence_invalid",
  "manifest_markdown_hash_mismatch",
  "manifest_professor_pdf_hash_mismatch",
  "manifest_publish_gate_invalid",
  "manifest_question_items_hash_mismatch",
  "manifest_question_items_invalid",
  "manifest_shared_questions_contract_invalid",
  "manifest_shared_questions_contract_mismatch",
  "manifest_storage_release_bind_failed",
  "manifest_storage_release_invalid",
  "manifest_storage_release_mismatch",
  "manifest_student_pdf_hash_mismatch",
  "manifest_student_render_projection_mismatch",
  "manifest_student_renderer_invalid",
  "manifest_topic_plan_contract_invalid",
  "manifest_topic_plan_contract_mismatch",
  "manifest_university_generation_path_contract_invalid",
  "manifest_university_workflow_resolution_invalid",
  "manifest_version_invalid",
  "manifest_workflow_resolution_invalid",
  "no_grading_bundle_produced",
  "no_manifest_produced",
  "no_markdown_produced",
  "no_professor_pdf_produced",
  "no_starter_code_produced",
  "no_student_pdf_produced",
  "shared_questions_lineage_sync_failed",
  "student_responses_invalid",
  "student_responses_missing",
] as const

/**
 * The institution's course/profile registration or a deployment capability,
 * not anything the professor typed. Retrying cannot enable it; the action
 * is to have the course configuration reviewed.
 */
const CONFIGURATION_CODES = [
  "course_configuration_invalid",
  "course_context_invalid",
  "course_context_version_unsupported",
  "course_deterministic_template_unavailable",
  "course_profile_ambiguous",
  "course_profile_context_mismatch",
  "course_profile_not_registered",
  "education_level_deterministic_unavailable",
  "education_level_provider_mismatch",
  "organization_course_not_registered",
  "organization_course_profile_mismatch",
  "subject_family_not_registered",
  "university_workflow_resolution_failed",
  "unsupported_university_course",
] as const

/**
 * Genuinely transient: an LLM budget exhausted, a soft timeout, an
 * unexpected exception, or a dispatch-identity mismatch that a fresh
 * execution resolves. "Try again" is honest advice here — and ONLY here.
 */
const TEMPORARY_CODES = [
  "expression_reservation_soft_timeout",
  "grading_job_error",
  "material_id_mismatch",
  "pipeline_exception",
  "pipeline_retry_budget_exhausted",
  "student_id_mismatch",
  "task_index_mismatch",
] as const

/**
 * Codes WEB itself writes into `MaterialAssignment.error_code`, not the
 * engine. Three producers share that column — the engine's 91-code
 * registry, web's `/generate` request validator, and the reconciliation
 * sweeper — and nothing declared the web-side half, so reading a FAILED row
 * could not tell you who authored the code (audit F-ENG-3).
 *
 * They are kept unprefixed rather than namespaced as `web:*`: a prefix
 * would need a data migration for every stored row and buys nothing a test
 * cannot assert. `test_engine_error_taxonomy.py` enforces that this set and
 * the engine registry stay disjoint, which is the property that matters.
 *
 * All three are transient by construction: the sweeper force-fails an
 * assignment whose execution it cannot account for, and a retry
 * re-dispatches it.
 */
const WEB_ORIGIN_TEMPORARY_CODES = [
  // core/tasks.py — reconciliation sweeper: the execution identity was
  // never recorded, so we cannot prove what happened to it.
  "execution_untracked",
  // core/tasks.py — the Cloud Run execution reached a terminal state
  // without the engine ever calling back.
  "execution_terminated",
  // api/webhooks.py — a `failed` callback that carried no error_code at all.
  "unknown",
] as const

/**
 * PRD-65 — codes the `render_edited` compile job (ADR-0050) writes onto a
 * saved DRAFT, never onto the assignment's own `error_code`: the previous
 * release stays live. Declared apart from the four engine buckets above
 * because `test_engine_error_taxonomy.py` asserts those against the engine's
 * callback registry, which these are not in. The professor fixes the TEXT
 * for the first three (an `input` class: the same draft re-sent gets the same
 * answer); the two below are transient.
 */
const CONTENT_EDIT_INPUT_CODES = [
  // The draft refers to an item or option the content does not have.
  "edit_invalid",
  // `validate_sheet_report` refused the edited sheet.
  "edit_validation_failed",
  // A render guard would have rewritten the professor's text.
  "edit_render_rejected",
] as const

const CONTENT_EDIT_TEMPORARY_CODES = [
  // The release changed under the draft (a regeneration landed). Retry
  // re-reads the current content.
  "edit_source_mismatch",
  // core/tasks.py — the reconcile sweep timed the compile out.
  "engine_timeout",
] as const

const BUCKETS: ReadonlyArray<readonly [MaterialFailureKind, readonly string[]]> = [
  ["input", INPUT_REJECTION_CODES],
  ["input", CONTENT_EDIT_INPUT_CODES],
  ["quality", QUALITY_GATE_CODES],
  ["configuration", CONFIGURATION_CODES],
  ["temporary", TEMPORARY_CODES],
  ["temporary", WEB_ORIGIN_TEMPORARY_CODES],
  ["temporary", CONTENT_EDIT_TEMPORARY_CODES],
]

/**
 * Consecutive failures with the SAME code on one student's generation after
 * which the row offers "report" instead of "retry". A retry re-runs the
 * identical stored request; the same code twice in a row says the engine is
 * deterministic about it. 2026-09-21: one professor clicked Retry 17 times
 * on the same `manifest_invalid`.
 */
export const SAME_CODE_FAILURES_BEFORE_REPORT = 2

/** Every code this build knows, engine-origin and web-origin alike. */
export const KNOWN_ENGINE_ERROR_CODES: readonly string[] = BUCKETS.flatMap(([, codes]) => codes)

/** The web-minted subset, so the backend test can assert disjointness. */
export const WEB_ORIGIN_ERROR_CODES: readonly string[] = WEB_ORIGIN_TEMPORARY_CODES

/**
 * Classify an engine failure code.
 *
 * An unrecognized code is `"unknown"`, NOT `"temporary"`: we cannot promise
 * a retry will help when we do not know what failed. The raw string is
 * still never rendered — a teacher saw `task_index_mismatch` in production
 * on 2026-08-04, which is what this module exists to prevent.
 */
export function materialFailureKind(code: string | null | undefined): MaterialFailureKind {
  if (!code) return "temporary"
  for (const [kind, codes] of BUCKETS) {
    if ((codes as readonly string[]).includes(code)) return kind
  }
  return "unknown"
}

export function isInputEngineError(code: string | null | undefined): boolean {
  return materialFailureKind(code) === "input"
}

/**
 * True when retrying the same request cannot possibly succeed, so the UI
 * must offer a corrective action instead of a retry button.
 *
 * `unknown` counts: an unclassified code is more likely to be a new
 * permanent rejection than a new transient one, and offering a retry that
 * re-runs the same rejection forever is the worse failure.
 */
export function isUnretryableEngineError(code: string | null | undefined): boolean {
  const kind = materialFailureKind(code)
  return kind === "input" || kind === "configuration" || kind === "unknown"
}
